import { motion, useReducedMotion } from "framer-motion";
import { Rocket } from "lucide-react";
import React, { useEffect, useState } from "react";
import TwinklingStars from "./TwinklingStars";

type LaunchPhase = "idle" | "launch" | "exit";

interface RocketLaunchLoaderProps {
  message?: string;
  /** When true, plays the final takeoff and calls onLaunchComplete. */
  ready?: boolean;
  onLaunchComplete?: () => void;
}

const RocketLaunchLoader: React.FC<RocketLaunchLoaderProps> = ({
  message = "Preparing for launch…",
  ready = false,
  onLaunchComplete,
}) => {
  const reduceMotion = useReducedMotion();
  const [phase, setPhase] = useState<LaunchPhase>("idle");

  useEffect(() => {
    if (ready && phase === "idle") {
      setPhase("launch");
    }
  }, [ready, phase]);

  useEffect(() => {
    if (!reduceMotion || !ready) return;
    const timer = window.setTimeout(() => onLaunchComplete?.(), 280);
    return () => window.clearTimeout(timer);
  }, [reduceMotion, ready, onLaunchComplete]);

  const handleRocketLaunchComplete = () => {
    if (phase === "launch") {
      setPhase("exit");
    }
  };

  const handleOverlayExitComplete = () => {
    if (phase === "exit") {
      onLaunchComplete?.();
    }
  };

  if (reduceMotion) {
    return (
      <div
        className="fixed inset-0 z-50 flex flex-col items-center justify-center bg-gradient-to-br from-indigo-900 via-gray-900 to-black"
        role="status"
        aria-live="polite"
        aria-busy={!ready}
      >
        <Rocket className="h-12 w-12 -rotate-45 text-indigo-300" aria-hidden />
        <p className="mt-6 text-lg text-white/90">
          {ready ? "Liftoff!" : message}
        </p>
      </div>
    );
  }

  const isLaunching = phase === "launch" || phase === "exit";
  const statusMessage = isLaunching ? "Liftoff!" : message;

  return (
    <motion.div
      className="fixed inset-0 z-50 overflow-hidden bg-gradient-to-br from-indigo-900 via-gray-900 to-black"
      role="status"
      aria-live="polite"
      aria-busy={phase !== "exit"}
      initial={{ opacity: 1 }}
      animate={{ opacity: phase === "exit" ? 0 : 1 }}
      transition={{ duration: 0.55, ease: "easeInOut" }}
      onAnimationComplete={handleOverlayExitComplete}
    >
      <div
        className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_at_50%_100%,rgba(99,102,241,0.25),transparent_55%)]"
        aria-hidden
      />

      <TwinklingStars />

      <div className="relative flex min-h-screen flex-col items-center justify-center px-6">
        <div className="relative h-56 w-40">
          <div
            className="absolute bottom-0 left-1/2 h-3 w-28 -translate-x-1/2 rounded-full bg-indigo-400/20 shadow-[0_0_24px_rgba(99,102,241,0.45)]"
            aria-hidden
          />
          <div
            className="absolute bottom-2 left-1/2 h-1 w-20 -translate-x-1/2 rounded-full bg-indigo-300/40"
            aria-hidden
          />

          {[0, 1, 2].map((i) => (
            <motion.div
              key={i}
              className="absolute bottom-6 left-[calc(50%-32px)] -translate-x-1/2 rounded-full blur-sm"
              style={{
                width: isLaunching ? 14 - i * 2 : 10 - i * 2,
                height: isLaunching ? 14 - i * 2 : 10 - i * 2,
                background:
                  i === 0
                    ? "rgba(251, 191, 36, 0.9)"
                    : i === 1
                      ? "rgba(249, 115, 22, 0.75)"
                      : "rgba(239, 68, 68, 0.5)",
              }}
              animate={
                isLaunching
                  ? { y: [0, 60 + i * 18], opacity: [1, 0], scale: [1, 0.2] }
                  : { y: [0, 14 + i * 6], opacity: [0.5, 0], scale: [1, 0.5] }
              }
              transition={
                isLaunching
                  ? {
                      duration: 0.7,
                      repeat: Infinity,
                      delay: i * 0.05,
                      ease: "easeOut",
                    }
                  : {
                      duration: 0.7,
                      repeat: Infinity,
                      delay: i * 0.1,
                      ease: "easeOut",
                    }
              }
              aria-hidden
            />
          ))}

          <motion.div
            className="absolute bottom-8 left-[calc(50%-32px)] -translate-x-1/2"
            initial={false}
            animate={
              phase === "idle"
                ? { y: [0, -5, 0], opacity: 1 }
                : phase === "launch"
                  ? {
                      y: [0, 10, -160, -560],
                      opacity: [1, 1, 1, 0],
                    }
                  : { y: -560, opacity: 0 }
            }
            transition={
              phase === "idle"
                ? { duration: 2.4, repeat: Infinity, ease: "easeInOut" }
                : phase === "launch"
                  ? {
                      duration: 1.2,
                      times: [0, 0.16, 0.5, 1],
                      ease: ["easeOut", "easeIn", "easeIn"],
                    }
                  : { duration: 0.3, ease: "easeIn" }
            }
            onAnimationComplete={handleRocketLaunchComplete}
            aria-hidden
          >
            <div className="relative flex flex-col items-center">
              <Rocket className="h-14 w-14 -rotate-45 text-white drop-shadow-[0_0_12px_rgba(255,255,255,0.45)]" />
              <motion.div
                className="h-8 w-3 rounded-b-full bg-gradient-to-b from-amber-300 via-orange-500 to-red-600 blur-[1px]"
                animate={{
                  scaleY: isLaunching ? [1, 1.6, 1.2] : [0.6, 1.1, 0.8],
                  opacity: isLaunching ? [1, 1, 0.6] : [0.7, 1, 0.75],
                }}
                transition={{
                  duration: isLaunching ? 0.12 : 0.22,
                  repeat: Infinity,
                  ease: "easeInOut",
                }}
                aria-hidden
              />
            </div>
          </motion.div>
          {/* Rocket Flame */}
          <motion.div
            className="absolute bottom-1 left-1/2 h-8 w-16 -translate-x-1/2 rounded-full bg-indigo-300/10 blur-md"
            animate={
              isLaunching
                ? { opacity: [0, 0.8, 0], scale: [0.8, 2, 2.8] }
                : { opacity: [0, 0.3, 0], scale: [0.9, 1.2, 1.4] }
            }
            transition={
              isLaunching
                ? { duration: 0.8, repeat: Infinity, ease: "easeOut" }
                : { duration: 2.4, repeat: Infinity, ease: "easeOut" }
            }
            aria-hidden
          />
        </div>

        <motion.p
          className="mt-2 text-center text-lg font-medium tracking-wide text-white/90"
          animate={{ opacity: [0.55, 1, 0.55] }}
          transition={{ duration: 2.2, repeat: Infinity, ease: "easeInOut" }}
        >
          {statusMessage}
        </motion.p>
        <p className="mt-2 text-sm text-indigo-200/60">
          {isLaunching ? "See you in orbit" : "Checking your session"}
        </p>
      </div>
    </motion.div>
  );
};

export default RocketLaunchLoader;
